import * as CL from '@design-system/component-library';
import { Link, generatePath, useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import {
  billingAccountMsg,
  billingAccountsMsg,
  companyMsg,
  dueDateMsg,
  invoicesMsg,
  invoicingMsg,
  omaElisaForCompaniesMsg,
  paidMsg,
  payerMsg,
  t,
} from '../../common/i18n/index.js';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import { formatTimestampToUTCDDMMYYYY } from '../../common/utils/dateUtils.js';
import { formatBalance, formatSum } from '../../common/utils/priceUtils.js';
import { highlightKeyword } from '../../common/utils/searchFieldUtils.js';
import { HeroHeading, HeroHeadingType } from '../HeroHeading/index.js';
import { getStatusColumnInvoiceState } from '../InvoiceDetails/InvoiceDetails.js';
import { LocalNavi } from '../LocalNavi/index.js';
import { Status } from '../Status/Status.js';
import { Table } from '../Table/Table.js';
import { TableUrlParams } from '../Table/tableConstants.js';
import type { CLTableColumn } from '../../common/types/componentLibrary.js';
import type { InvoicesSearchResponse } from '../../generated/api/invoicesSearchResponse.js';
import type { AuthenticatedUser } from '../../public/site/authState';
import type { BreadCrumbList } from '../BreadCrumbs/index.js';
import type { JSX, ReactNode } from 'react';

export const InvoiceSearchParams = {
  openInvoicesOnly: 'openInvoicesOnly',
  paidInvoicesOnly: 'paidInvoicesOnly',
};

type InvoiceCategory = 'open' | 'paid' | 'all';

const getInvoiceListCategory = (search?: string): InvoiceCategory => {
  if (search?.includes(InvoiceSearchParams.openInvoicesOnly)) {
    return 'open';
  } else if (search?.includes(InvoiceSearchParams.paidInvoicesOnly)) {
    return 'paid';
  }
  return 'all';
};

export enum InvoicesSceneTab {
  BILLING_ACCOUNTS = 'BILLING_ACCOUNTS',
  INVOICES = 'INVOICES',
}

interface InvoicesLocalNaviProps {
  children: ReactNode;
  hideHeadingAndTabs?: boolean;
  tab?: InvoicesSceneTab;
}

const getBreadCrumbPaths = (tab?: InvoicesSceneTab): BreadCrumbList | undefined => {
  if (tab === InvoicesSceneTab.INVOICES) {
    return [{ name: t.VCUZ(omaElisaForCompaniesMsg), path: paths.SELF_SERVICE_HOME }, { name: t.Y7C0(invoicesMsg) }];
  }
  if (tab === InvoicesSceneTab.BILLING_ACCOUNTS) {
    return [
      { name: t.VCUZ(omaElisaForCompaniesMsg), path: paths.SELF_SERVICE_HOME },
      { name: t.ZVMK(billingAccountsMsg) },
    ];
  }
  return;
};

export const Invoices = ({ children, tab }: InvoicesLocalNaviProps) => {
  return (
    <div className="of-invoices-scene">
      <HeroHeading
        breadCrumbPaths={getBreadCrumbPaths(tab)}
        heroHeadingType={HeroHeadingType.INVOICES}
        title={t.AUI8(invoicingMsg)}
      />
      <LocalNavi
        wide
        categories={[
          {
            children: t.Y7C0(invoicesMsg),
            to: paths.INVOICES,
          },
          {
            children: t.ZVMK(billingAccountsMsg),
            to: paths.BILLING_ACCOUNTS,
          },
          {
            children: t.OMMT('Invoice letters'),
            to: paths.INVOICE_DOCUMENTS,
          },
        ]}
      />
      {children}
    </div>
  );
};

export const getEmptyListText = (category: InvoiceCategory | '') => {
  switch (category) {
    case 'open':
      return `${t.A37D('No')} ${t.FOKG('open')} ${t.VME0('invoices')}`;
    case 'paid':
      return `${t.A37D('No')} ${t.QVCE('paid')} ${t.VME0('invoices')}`;
    default:
      return `${t.A37D('No')} ${t.VME0('invoices')}`;
  }
};

const appendInvoiceCategoryParam = (category: InvoiceCategory, params: URLSearchParams) => {
  switch (category) {
    case 'open':
      params.delete('paidInvoicesOnly');
      params.set('openInvoicesOnly', 'true');
      break;
    case 'paid':
      params.delete('openInvoicesOnly');
      params.set('paidInvoicesOnly', 'true');
      break;
    default:
      params.delete('paidInvoicesOnly');
      params.delete('openInvoicesOnly');
  }
  return params;
};

type ColumnKey = 'invoiceDisplayId' | 'payerName' | 'due' | 'sum' | 'state' | 'companyName' | 'billingAccountDisplayId';
type ColumnKeys = [ColumnKey?, ColumnKey?, ColumnKey?, ColumnKey?, ColumnKey?, ColumnKey?] & string[];

interface InvoiceTableProps {
  invoices: InvoicesSearchResponse[];
  search: string;
  authenticatedUser: AuthenticatedUser | undefined;
  tableColumns?: ColumnKeys;
}

export const InvoiceTable = ({ invoices, search, authenticatedUser, tableColumns }: InvoiceTableProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const category = getInvoiceListCategory(search);

  const onStateChange = (stateOption = ''): void => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set(TableUrlParams.PAGE, '1');
    queryParams.set(TableUrlParams.OFFSET, '0');
    appendInvoiceCategoryParam(stateOption as InvoiceCategory, queryParams);
    navigate(`${location.pathname}?${queryParams}`);
  };

  const columns: CLTableColumn[] = [
    {
      key: 'invoiceDisplayId',
      label: t.BRFX('Invoice number'),
      sortable: true,
    },
    { key: 'billingAccountDisplayId', label: `${t.IFT9(billingAccountMsg)} *`, sortable: true },
    { key: 'payerName', label: `${t.PB6S(payerMsg)} *`, sortable: true },
    { key: 'due', label: t.LA93(dueDateMsg), sortable: true },
    {
      key: 'sum',
      label: t.P4RQ('Amount'),
      sortable: true,
    },
    {
      key: 'state',
      label: (
        <CL.Dropdown
          key={category}
          items={[
            { label: t.EY6A('All'), value: 'all' },
            { label: t.GMAP('Open'), value: 'open' },
            { label: t.UQHZ('Paid'), value: 'paid' },
          ]}
          aria-label="state"
          selectedValue={category}
          className="of-table-header-dropdown"
          integrated={true}
          onValueChange={value => {
            onStateChange(value?.dataset?.value);
          }}
        />
      ),
      sortable: false,
    },
    { key: 'companyName', label: t.KJTS(companyMsg), sortable: false },
  ].filter(col => !tableColumns || tableColumns.includes(col.key));

  const rows = invoices?.map(invoiceSearchResponse => {
    const invoice = invoiceSearchResponse.result;
    const matchedFields = invoiceSearchResponse.matchedFields;
    const fields = {
      billingAccountDisplayId: (
        <span>
          {invoice?.billingAccountDisplayId}
          <br />
          <div className={dsClass.FONT_SIZE_SMALL}>{invoice.billingAccountName}</div>
          <div className={dsClass.FONT_SIZE_SMALL}>{invoice?.billingAccountNameExtension}</div>
        </span>
      ),
      payerName: (
        <span>
          {invoice?.payerName}
          <div className={dsClass.FONT_SIZE_SMALL}>{invoice?.payerNameExtension}</div>
        </span>
      ),
      invoiceDisplayId: (
        <>
          <Link
            to={`${generatePath(paths.INVOICE, { invoiceId: invoice.invoiceDisplayId })}?mdmId=${
              invoice.accountMasterId
            }`}
          >
            {matchedFields?.includes('invoiceDisplayId')
              ? highlightKeyword(invoice.invoiceDisplayId, search)
              : invoice.invoiceDisplayId}
          </Link>
          {search &&
            matchedFields?.includes('invoiceDisplayId') &&
            (!tableColumns || tableColumns.includes('billingAccountDisplayId')) && (
              <div className={dsClass.FONT_SIZE_SMALL}>
                {t.IFT9(billingAccountMsg)}: {highlightKeyword(invoice.billingAccountDisplayId || '', search)}
              </div>
            )}
        </>
      ),
      state: (
        <Status
          textAfterBadge={getStatusColumnInvoiceState(invoice.balance || 0, invoice.due).text}
          color={getStatusColumnInvoiceState(invoice.balance || 0, invoice.due).color}
          inTable={true}
        />
      ),
      due: formatTimestampToUTCDDMMYYYY(invoice.due),
      sum: (
        <span>
          {formatSum(invoice.sum)}
          <div className={dsClass.FONT_SIZE_SMALL}>
            {t.RJ27(paidMsg)} {formatBalance(invoice.sum, invoice.balance || 0)}
          </div>
        </span>
      ),
      companyName: getCompanyName(authenticatedUser, invoice.accountMasterId),
    } as { [key: string]: JSX.Element | string };

    return !tableColumns
      ? fields
      : tableColumns.reduce((reducedFields: { [key: string]: JSX.Element | string }, column: string) => {
          reducedFields[column] = fields[column];
          return reducedFields;
        }, {});
  });

  return (
    <>
      <Table columns={columns} rows={rows} noItemsText={getEmptyListText('')} />
      {category !== 'all' && rows?.length === 0 && (
        <p>
          {category === 'open' && (
            <CL.Button
              onClick={() => navigate(`${pathname}?${appendInvoiceCategoryParam('paid', new URLSearchParams(search))}`)}
            >
              {t.YFT1('Go to paid invoices')}
            </CL.Button>
          )}
          {category === 'paid' && (
            <CL.Button
              onClick={() => navigate(`${pathname}?${appendInvoiceCategoryParam('open', new URLSearchParams(search))}`)}
            >
              {t.FTZT('Go to open invoices')}
            </CL.Button>
          )}
        </p>
      )}
    </>
  );
};
